import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

export const GroupRowSchema = z.object({
  id: z.string(),
  /** Kod grupy — TEKST; u klientow zwykle slowo (BIURO, PRODU). */
  spsGroupNumber: z.string(),
  name: z.string(),
  memberCount: z.number(),
  machineCount: z.number(),
  /** Ile reguł limitów celuje w tę grupę — zmiana składu przesuwa te limity. */
  rationingCount: z.number(),
  pending: z.boolean(),
});

export type GroupRow = z.infer<typeof GroupRowSchema>;

const GroupsPageSchema = z.object({
  rows: z.array(GroupRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export const MemberSchema = z.object({
  id: z.string(),
  /** NUMER KARTY (Users.USERNUMBER) — tekst, z wiodacymi zerami. */
  spsUserNumber: z.string(),
  name: z.string(),
  active: z.boolean(),
  /** Users.RATIONEFFECT — 1 albo 2 znaczy, ze automat NIE pilnuje limitow tej osoby. */
  rationEffect: z.number(),
  machineCount: z.number(),
});

export type Member = z.infer<typeof MemberSchema>;

const MembersSchema = z.object({
  group: z.object({ id: z.string(), spsGroupNumber: z.string(), name: z.string() }),
  members: z.array(MemberSchema),
});

export const CandidateSchema = z.object({
  id: z.string(),
  spsUserNumber: z.string(),
  name: z.string(),
});

export type Candidate = z.infer<typeof CandidateSchema>;

export type ListGroupsParams = {
  groupId: string;
  search?: string;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
};

export const listGroups = (p: ListGroupsParams) => {
  const q = new URLSearchParams({
    groupId: p.groupId,
    page: String(p.page),
    pageSize: String(p.pageSize),
    sort: p.sort,
    dir: p.dir,
  });
  if (p.search) q.set('search', p.search);
  return apiGet(`/employee-groups?${q.toString()}`, GroupsPageSchema);
};

/**
 * Czy kod grupy jest wolny.
 *
 * ZASTAPILO `fetchNextGroupId`. Kod grupy to zwykle slowo nadawane przez czlowieka, wiec
 * MAX+1 nie mialo sensu — mozna tylko sprawdzic kolizje, i to z uwzglednieniem kolacji SPS.
 */
export const checkGroupCode = (groupId: string, code: string) =>
  apiGet(
    `/employee-groups/check-code?groupId=${groupId}&code=${encodeURIComponent(code)}`,
    z.object({
      code: z.string(),
      free: z.boolean(),
      takenBy: z.object({ id: z.string(), name: z.string(), spsGroupNumber: z.string() }).nullable(),
      pending: z.boolean(),
    }),
  );

export const fetchMembers = (id: string) => apiGet(`/employee-groups/${id}/members`, MembersSchema);

/** Kursor keyset — ostatnia zobaczona para `(name, id)`. `null` znaczy koniec listy. */
export type KursorKandydatow = { afterName: string; afterId: string } | null;

const CandidatesPageSchema = z.object({
  rows: z.array(CandidateSchema),
  /** Ilu jest WSZYSTKICH pasujących — bez tego ekran udawałby, że pokazał komplet. */
  total: z.number(),
  next: z.object({ afterName: z.string(), afterId: z.string() }).nullable(),
});

/**
 * Kandydaci do grupy, porcjami.
 *
 * ⚠️ KURSOR, NIE NUMER STRONY. Zbiór „pracownicy spoza tej grupy" kurczy się w trakcie
 * przeglądania, więc `skip: N` przeskoczyłby osobę, która wskoczyła na zwolnione miejsce —
 * a brakująca osoba nie wygląda inaczej niż osoba, której po prostu nie ma.
 */
export const fetchCandidates = (id: string, groupId: string, search: string, kursor?: KursorKandydatow) => {
  const q = new URLSearchParams({ groupId, limit: '50' });
  if (search) q.set('search', search);
  if (kursor) {
    q.set('afterName', kursor.afterName);
    q.set('afterId', kursor.afterId);
  }
  return apiGet(`/employee-groups/${id}/candidates?${q.toString()}`, CandidatesPageSchema);
};

export const WithoutGroupRowSchema = z.object({
  id: z.string(),
  spsUserNumber: z.string(),
  name: z.string(),
  /** Users.RATIONEFFECT — 1 albo 2 znaczy, że automat NIE pilnuje limitów tej osoby. */
  rationEffect: z.number(),
  machineCount: z.number(),
});

export type WithoutGroupRow = z.infer<typeof WithoutGroupRowSchema>;

/**
 * Pracownicy poza WSZYSTKIMI grupami.
 *
 * ⚠️ To nie jest widok „dla porządku". Reguły limitów celują między innymi w grupy, więc osoba
 * poza wszystkimi grupami nie jest objęta żadną regułą grupową — i nie było ekranu, na którym
 * dałoby się to zobaczyć.
 */
export const fetchWithoutGroup = (groupId: string, p: { search?: string; page: number; pageSize: number }) => {
  const q = new URLSearchParams({ groupId, page: String(p.page), pageSize: String(p.pageSize) });
  if (p.search) q.set('search', p.search);
  return apiGet(
    `/employee-groups/bez-grupy?${q.toString()}`,
    z.object({
      rows: z.array(WithoutGroupRowSchema),
      total: z.number(),
      page: z.number(),
      pageSize: z.number(),
    }),
  );
};

export const createGroup = (payload: {
  groupId: string;
  /** Kod grupy jest WYMAGANY — portal go nie generuje. */
  spsGroupNumber: string;
  name: string;
  memberIds: string[];
  /**
   * ⚠️ UPRAWNIENIA W TYM SAMYM ŻĄDANIU. API je przyjmuje (`CreateGroupSchema.operacje`), więc
   * grupa nie istnieje ani chwili bez nich — dwa żądania dawałyby stan połowiczny przy błędzie
   * drugiego, czyli grupę bez uprawnień, o której nikt nie wie, że jest niepełna.
   */
  operacje?: string[];
}) =>
  apiSend(
    'POST',
    '/employee-groups',
    payload,
    z.object({ opId: z.string(), id: z.string(), spsGroupNumber: z.string() }),
  );

export const updateGroup = (id: string, name: string) =>
  apiSend('PATCH', `/employee-groups/${id}`, { name }, z.object({ opId: z.string() }));

export const updateMembers = (id: string, payload: { add: string[]; remove: string[] }) =>
  apiSend('POST', `/employee-groups/${id}/members`, payload, z.object({ opId: z.string() }));

/**
 * ZMIANA KODU GRUPY — osobna trasa, nie `PATCH`.
 *
 * ⚠️ `UserGroups.USERGROUPNUMBER` jest kluczem naturalnym rozsianym po sześciu tabelach SPS:
 * członkostwa, dostęp do automatów, dozwolone operacje oraz limit i dostęp do towaru celujące
 * w tę grupę. To przeniesienie klucza, a nie edycja pola — dlatego własne okno i własna trasa.
 *
 * ⚠️ HISTORII WYDAŃ TO NIE RUSZA i nie ma tu takiej opcji: `TransactionLog` trzyma grupy
 * zamrożone w chwili wydania w dziesięciu kolumnach, a SPS też ich nie przepisuje. Raport
 * historyczny zostaje przy kodzie, który wtedy obowiązywał.
 */
export const zmienKodGrupy = (id: string, payload: { groupId: string; nowyKod: string }) =>
  apiSend(
    'POST',
    `/employee-groups/${id}/code`,
    payload,
    z.object({ opId: z.string(), stary: z.string(), nowy: z.string() }),
  );

// ─────────────────────── dostęp grupy do automatów ───────────────────────────

/**
 * DOSTĘP GRUPY DO AUTOMATÓW (`UserGroups_VendingMachineAssignment`).
 *
 * ⚠️ TEN SAM ZBIÓR DANYCH JEST NA PRZYPISANIACH, tylko od strony automatu. Nie jest to druga
 * funkcja ani druga tabela — to jedno powiązanie oglądane z dwóch końców. Zapis z obu stron
 * kolejkuje identyczne zmiany.
 *
 * ⚠️ ODDAJEMY WSZYSTKIE AUTOMATY WYSPY, nie tylko przypisane. Lista samych przypisanych nie
 * odpowiada na drugą połowę pytania — „gdzie ta grupa NIE ma dostępu" — a właśnie po to ktoś
 * to okno otwiera.
 */
export const GroupMachineSchema = z.object({
  id: z.string(),
  /** `VendingMachines.VMID` — tym SPS adresuje automat. */
  vmid: z.number(),
  name: z.string(),
  assigned: z.boolean(),
});

export type GroupMachine = z.infer<typeof GroupMachineSchema>;

const GroupMachinesSchema = z.object({
  group: z.object({
    id: z.string(),
    spsGroupNumber: z.string(),
    name: z.string(),
    /** Liczebność grupy — tyle osób wpuszcza na automat jedno zaznaczenie. */
    memberCount: z.number(),
  }),
  machines: z.array(GroupMachineSchema),
});

export const fetchGroupMachines = (id: string, groupId: string) =>
  apiGet(`/employee-groups/${id}/machines?groupId=${groupId}`, GroupMachinesSchema);

/**
 * ⚠️ RÓŻNICA, NIE LISTA DOCELOWA. Dwie osoby na tym samym ekranie nadpisywałyby sobie stan;
 * „dołóż ten automat" sumuje się z „zabierz tamten", a „mają być dokładnie te" nie.
 */
export const updateGroupMachines = (id: string, payload: { add: string[]; remove: string[] }) =>
  apiSend('POST', `/employee-groups/${id}/machines`, payload, z.object({ opId: z.string() }));

/**
 * USUNIĘCIE GRUPY — podgląd skutków i blokad (13.10.2026). Reguły w API (`usuniecie-grupy.ts`).
 *
 * ⚠️ ŁAGODNIE (`passthrough`): okno pytania nie może się wywrócić na nowym polu po stronie API.
 */
const AutomatSchema = z.object({ vmid: z.number(), automat: z.string().nullable() });
export const SkutkiUsunieciaSchema = z
  .object({
    grupa: z.object({ id: z.string(), kod: z.string(), nazwa: z.string() }),
    czlonkowie: z.object({
      liczba: z.number(),
      bezInnejGrupy: z.number(),
      probka: z.array(z.object({ numer: z.string(), nazwa: z.string() })),
    }),
    automaty: z.array(AutomatSchema),
    dostep: z.object({ tracaAutomat: z.number(), bezZadnegoAutomatu: z.number() }),
    operacje: z.array(z.string()),
    limity: z.array(z.object({ itemType: z.number(), itemKey: z.string(), maxQty: z.number() }).passthrough()),
    uprawnienia: z.array(z.object({ rItem: z.string(), dItem: z.string() }).passthrough()),
    blokady: z.object({
      szuflady: z.array(AutomatSchema.extend({ beben: z.number(), szuflada: z.number() })),
      klapy: z.array(AutomatSchema.extend({ szuflada: z.number(), kanal: z.number(), klapa: z.number() })),
      pozycje: z.array(AutomatSchema.extend({ towar: z.string(), role: z.array(z.string()) })),
      raporty: z.array(z.object({ id: z.string(), nazwa: z.string() })),
      wKolejce: z.boolean(),
    }),
    hrPlus: z.object({ wlaczona: z.boolean(), czlonkowZHr: z.number(), wWyjatkach: z.boolean() }),
    wolnoUsunac: z.boolean(),
  })
  .passthrough();
export type SkutkiUsuniecia = z.infer<typeof SkutkiUsunieciaSchema>;

export const fetchSkutkiUsuniecia = (id: string, groupId: string) =>
  apiGet(`/employee-groups/${id}/delete-impact?groupId=${groupId}`, SkutkiUsunieciaSchema);

export const usunGrupe = (id: string, groupId: string) =>
  apiSend('DELETE', `/employee-groups/${id}?groupId=${groupId}`, undefined, z.object({ opIds: z.array(z.string()) }));
